/**
 * Cross-platform clipboard utility with iOS Safari fallback
 */

export interface ClipboardResult {
  success: boolean;
  error?: string;
}

/**
 * iOS Safari specific copy method
 * Uses proper iOS-compatible element creation and selection
 */
function tryIOSCopy(text: string): ClipboardResult {
  try {
    // Create a div with contenteditable for better iOS support
    const container = document.createElement('div');
    container.contentEditable = 'true';
    container.innerText = text;

    // Style for iOS Safari compatibility
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    container.setAttribute('readonly', '');

    document.body.appendChild(container);

    // iOS Safari requires focus and selection
    container.focus();

    const range = document.createRange();
    range.selectNodeContents(container);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);

    const success = document.execCommand('copy');
    document.body.removeChild(container);

    if (success) {
      return { success: true };
    }
    return {
      success: false,
      error: 'iOS copy command failed',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'iOS copy error',
    };
  }
}

/**
 * Fallback copy method using deprecated execCommand
 * Works better on iOS Safari in some contexts
 */
function tryFallbackCopy(text: string): ClipboardResult {
  try {
    // For iOS, use the iOS-specific method
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      return tryIOSCopy(text);
    }

    // Create temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Style to make it invisible but still selectable
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    textArea.style.opacity = '0';
    textArea.setAttribute('readonly', '');

    document.body.appendChild(textArea);

    // Select and copy
    textArea.select();
    textArea.setSelectionRange(0, text.length);

    const success = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (success) {
      return { success: true };
    }
    return {
      success: false,
      error: 'Copy command failed',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Detect iOS devices more reliably
 */
function isIOS(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * Copies text to clipboard with iOS Safari compatibility
 * Uses modern Clipboard API when available, falls back to legacy methods
 */
export async function copyToClipboard(text: string): Promise<ClipboardResult> {
  // For iOS devices, always try fallback first as it's more reliable
  if (isIOS()) {
    const fallbackResult = tryFallbackCopy(text);
    if (fallbackResult.success) {
      return fallbackResult;
    }
    // If fallback failed on iOS, try modern API as last resort
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error: `iOS copy failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
      }
    }
    return fallbackResult;
  }

  // For non-iOS devices, try modern Clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch {
      // If modern API fails, try fallback
      return tryFallbackCopy(text);
    }
  }

  // Use fallback for older browsers or insecure contexts
  return tryFallbackCopy(text);
}

/**
 * Copy URL immediately without waiting for share API
 * This function constructs the share URL directly for immediate copying
 */
export async function copyShareUrlImmediately(
  fileId: string,
  shareId?: string,
): Promise<ClipboardResult> {
  try {
    // Import the getShareUrl utility dynamically to avoid circular imports
    const { getShareUrl } = await import('../lib/routingUtils');

    if (shareId) {
      // If we already have a shareId, copy immediately
      const url = getShareUrl(shareId);
      return await copyToClipboard(url);
    }

    // If no shareId, we need to generate the share URL
    // For now, return an error indicating we need to share first
    return {
      success: false,
      error: 'No share ID available. File needs to be shared first.',
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to copy share URL',
    };
  }
}

/**
 * Checks if clipboard functionality is available
 */
export function isClipboardSupported(): boolean {
  return !!(navigator.clipboard || document.execCommand);
}
